import React, { useState, useEffect } from 'react';

const NaturalLanguageInput = ({ onAnswerKeyChange, onStudentWorkChange, initialData }) => {
  const [answerKeyText, setAnswerKeyText] = useState('');
  const [studentWorkText, setStudentWorkText] = useState('');
  const [parsingError, setParsingError] = useState(null);
  const [inputMode, setInputMode] = useState('structured'); // 'structured', 'freeform', 'mixed'
  const [autoDetectFormat, setAutoDetectFormat] = useState(true);

  // Multiple example formats
  const examples = {
    structured: `QUESTION 1: Explain the concept of photosynthesis.
CORRECT ANSWER: Photosynthesis is the process by which plants convert light energy into chemical energy, producing oxygen and glucose from carbon dioxide and water.
MAX SCORE: 10
RUBRIC: Accuracy (40%), Completeness (30%), Clarity (20%), Originality (10%)
KEYWORDS: photosynthesis, light energy, chemical energy, glucose, oxygen
DIFFICULTY: Medium

QUESTION 2: What are the main stages of cellular respiration?
CORRECT ANSWER: The main stages are glycolysis, the Krebs cycle, and the electron transport chain.
MAX SCORE: 8
RUBRIC: Accuracy (50%), Completeness (30%), Clarity (20%)
PARTIAL CREDIT: 2 points for each correct stage mentioned`,

    freeform: `Biology Quiz - Cellular Processes

1. Photosynthesis question:
Students should explain how plants make food using sunlight. Key points include light energy conversion, CO2 + H2O → glucose + O2, chloroplasts. Worth 10 points total.

2. Respiration stages (8 pts):
Must mention glycolysis (happens in cytoplasm), Krebs cycle (mitochondria), electron transport chain (inner membrane). Give partial credit for incomplete answers.`,

    mixed: `EXAM: Biology Fundamentals
TOTAL POINTS: 100
TIME LIMIT: 60 minutes

Q1 (Multiple Choice - 5 pts): Which organelle is responsible for photosynthesis?
A) Mitochondria B) Chloroplast C) Nucleus D) Ribosome
ANSWER: B
EXPLANATION: Chloroplasts contain chlorophyll and are where photosynthesis occurs.

Q2 (Short Answer - 15 pts): Describe the process of cellular respiration.
SAMPLE ANSWER: Cellular respiration breaks down glucose to produce ATP through three stages: glycolysis, Krebs cycle, and electron transport chain.
GRADING: Full credit requires all three stages (5 pts each)

Q3 (Essay - 25 pts): Compare and contrast photosynthesis and cellular respiration.
RUBRIC:
- Identifies key processes (30%)
- Explains similarities (25%) 
- Explains differences (25%)
- Writing quality (20%)
MINIMUM LENGTH: 200 words`
  };

  const exampleStudentWork = {
    structured: `STUDENT: John Doe
STUDENT ID: 12345
CLASS: Biology 101
SUBMISSION DATE: 2024-03-15

QUESTION 1 ANSWER: Photosynthesis is when plants use sunlight to make food. They take in carbon dioxide and release oxygen. It happens in the leaves.

QUESTION 2 ANSWER: Cellular respiration has glycolysis, Krebs cycle, and something with electrons. I think it makes ATP.`,

    freeform: `Student: Jane Smith

1. Plants do photosynthesis to make their own food. They use the sun's energy and carbon dioxide from the air, plus water from their roots. This makes glucose (sugar) and oxygen comes out as waste.

2. For cellular respiration, first there's glycolysis which breaks down glucose. Then the Krebs cycle happens in the mitochondria. The last part is the electron transport chain but I'm not sure exactly what it does.`,

    mixed: `Name: Alex Johnson
Student ID: 67890
Date: March 15, 2024

1. B - Chloroplast

2. Cellular respiration is how cells get energy from food. It starts with glycolysis in the cytoplasm where glucose gets broken down. Then there's the Krebs cycle in the mitochondria. The last step is electron transport which makes most of the ATP.

3. Photosynthesis and cellular respiration are opposite processes. Photosynthesis takes in CO2 and water to make glucose and oxygen using light energy. Cellular respiration takes glucose and oxygen to make CO2, water, and ATP. Plants do both but animals only do respiration. Both involve chemical reactions and energy changes. Photosynthesis stores energy while respiration releases it.`
  };

  const parseNaturalLanguage = (text, type) => {
    try {
      if (type === 'answerKey') {
        return parseAnswerKey(text);
      } else {
        return parseStudentWork(text);
      }
    } catch (error) {
      console.error('Parsing error:', error);
      throw new Error(`Failed to parse ${type}: ${error.message}`);
    }
  };

  const parseAnswerKey = (text) => {
    const questions = [];
    let examMetadata = {};
    
    // Extract exam-level metadata
    const examTitleMatch = text.match(/EXAM:\s*(.+)/i);
    const totalPointsMatch = text.match(/TOTAL POINTS:\s*(\d+)/i);
    const timeLimitMatch = text.match(/TIME LIMIT:\s*(.+)/i);
    
    if (examTitleMatch) examMetadata.title = examTitleMatch[1].trim();
    if (totalPointsMatch) examMetadata.totalPoints = parseInt(totalPointsMatch[1]);
    if (timeLimitMatch) examMetadata.timeLimit = timeLimitMatch[1].trim();

    // Check if this is freeform format (no explicit QUESTION/ANSWER labels)
    const isFreeform = !/(?:QUESTION|Q)\s*\d+|CORRECT ANSWER|ANSWER:/i.test(text) && /^\d+\./m.test(text);
    
    if (isFreeform) {
      // Freeform parsing - extract answers from natural descriptions
      return parseFreeformAnswerKey(text, examMetadata);
    }

    // Structured parsing - split into question blocks
    const questionBlocks = text.split(/(?=(?:QUESTION|Q)\s*\d+|^\d+\.)/m).filter(block => block.trim());
    
    questionBlocks.forEach((block, index) => {
      if (!block.trim()) return;
      
      const lines = block.split('\n').map(line => line.trim()).filter(line => line);
      const question = parseQuestionBlock(lines, index + 1);
      
      if (question) {
        questions.push(question);
      }
    });

    return { examMetadata, questions };
  };

  // Parse freeform answer key where answers are embedded in descriptions
  const parseFreeformAnswerKey = (text, examMetadata) => {
    const questions = [];
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    let currentQuestion = null;
    let questionText = '';
    let answerText = '';
    let inAnswerSection = false;
    
    lines.forEach((line, index) => {
      // Detect question start - numbered items
      const questionMatch = line.match(/^(\d+)\.\s*(.+)/);
      
      if (questionMatch) {
        // Save previous question if exists
        if (currentQuestion && questionText) {
          // Store full description for grading context
          const fullDescription = (questionText + ' ' + answerText).trim();
          currentQuestion.fullDescription = fullDescription;

          // Detect question type from the full description
          currentQuestion.questionType = detectQuestionTypeFromFreeform(fullDescription);

          // Try to extract answer from the description
          const extractedAnswer = extractAnswerFromFreeform(fullDescription);
          if (extractedAnswer) {
            currentQuestion.correctAnswer = extractedAnswer;
          } else {
            // If no answer extracted, use full description as the answer criteria
            currentQuestion.correctAnswer = fullDescription;
          }
          questions.push(currentQuestion);
        }

        // Start new question
        const questionNum = questionMatch[1];
        const restOfLine = questionMatch[2];

        // Extract points if mentioned
        const pointsMatch = restOfLine.match(/(\d+)\s*(?:pts?|points?)/i);
        const maxScore = pointsMatch ? parseInt(pointsMatch[1]) : 10;

        currentQuestion = {
          questionNumber: questionNum,
          question: restOfLine,
          questionType: 'short_answer', // Will be detected later
          maxScore: maxScore,
          rubric: { accuracy: 40, completeness: 30, clarity: 20, originality: 10 },
          correctAnswer: '',
          fullDescription: '' // Will store the complete description for grading
        };

        questionText = restOfLine;
        answerText = '';
        inAnswerSection = false;
        return;
      }
      
      // Continue building question/answer text
      if (currentQuestion) {
        // Look for answer indicators in freeform
        if (line.match(/answer|should|must|include|key points|students should/i)) {
          inAnswerSection = true;
        }
        
        if (inAnswerSection || line.length > 20) {
          // This might be part of the answer description
          answerText += (answerText ? ' ' : '') + line;
        } else {
          questionText += ' ' + line;
        }
      }
    });
    
    // Save last question
    if (currentQuestion) {
      currentQuestion.question = questionText.trim();

      // Store full description for grading context
      const fullDescription = (questionText + ' ' + answerText).trim();
      currentQuestion.fullDescription = fullDescription;

      // Detect question type from the full description
      currentQuestion.questionType = detectQuestionTypeFromFreeform(fullDescription);

      const extractedAnswer = extractAnswerFromFreeform(fullDescription);
      if (extractedAnswer) {
        currentQuestion.correctAnswer = extractedAnswer;
      } else {
        // If no answer extracted, use full description as the answer criteria
        currentQuestion.correctAnswer = fullDescription;
      }
      questions.push(currentQuestion);
    }

    return { examMetadata, questions };
  };

  // Extract answer from freeform text description
  const extractAnswerFromFreeform = (text) => {
    if (!text) return '';
    
    // Look for explicit answer patterns
    const explicitAnswer = text.match(/(?:answer|correct answer|should be|is):\s*(.+?)(?:\.|$)/i);
    if (explicitAnswer) {
      return explicitAnswer[1].trim();
    }
    
    // Look for key points or requirements - these are often the "answer"
    const keyPointsMatch = text.match(/(?:key points?|must|should|include|key points? include):\s*(.+?)(?:\.|worth|pts?|points?|$)/i);
    if (keyPointsMatch) {
      return keyPointsMatch[1].trim();
    }
    
    // For multiple choice, look for letter answers (ANSWER: B or just B)
    const mcAnswerExplicit = text.match(/(?:answer|correct):\s*([A-D])/i);
    if (mcAnswerExplicit) {
      return mcAnswerExplicit[1].toUpperCase();
    }
    
    const mcAnswerStandalone = text.match(/^\s*([A-D])\s*[-–—]?\s*$/i);
    if (mcAnswerStandalone) {
      return mcAnswerStandalone[1].toUpperCase();
    }
    
    // Look for answer embedded in description
    // Pattern: "Students should explain [answer concept]"
    const descriptionMatch = text.match(/(?:students should|should|must|explain|describe|discuss|define)\s+(.+?)(?:\.|key|must|should|include|worth|pts?|points?|$)/i);
    if (descriptionMatch) {
      const extracted = descriptionMatch[1].trim();
      // If it's a reasonable length (not too long), use it
      if (extracted.length < 200) {
        return extracted;
      }
    }
    
    // Look for chemical/process descriptions (common in science)
    // Pattern: "CO2 + H2O → glucose + O2" or similar
    const processMatch = text.match(/([A-Z0-9\s\+\-→=]+(?:→|→|→)[A-Z0-9\s\+\-]+)/);
    if (processMatch) {
      return processMatch[1].trim();
    }
    
    // If we have the full text but no clear answer, use the most informative part
    // Take the sentence that contains key terms
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 10);
    if (sentences.length > 0) {
      // Find sentence with most key terms (process, explain, describe, etc.)
      const keyTermSentences = sentences.filter(s => 
        /(?:explain|describe|define|process|how|what|why|key|include|must|should)/i.test(s)
      );
      if (keyTermSentences.length > 0) {
        return keyTermSentences[0].trim();
      }
      // Otherwise use the first substantial sentence
      return sentences[0].trim();
    }
    
    // Return empty - the full text will be used as context for grading
    return '';
  };

  // Detect question type from freeform text description
  const detectQuestionTypeFromFreeform = (text) => {
    if (!text) return 'short_answer';

    // Essay indicators - long form, explain in detail, discuss, analyze
    const essayPatterns = /\b(essay|paragraph|discuss in detail|analyze|explain thoroughly|write about|elaborate|in-depth|comprehensive|detailed explanation|multiple paragraphs)\b/i;
    if (essayPatterns.test(text)) {
      return 'essay';
    }

    // Mathematical indicators - numbers, equations, calculate, solve
    const mathPatterns = /\b(calculate|solve|compute|equation|formula|derivative|integral|sum|product|multiply|divide|subtract|add|equals|=|\+|-|×|÷|√|∑|∫|sin|cos|tan|log|ln)\b/i;
    const hasNumbers = /\d+\s*[+\-*/=]\s*\d+/.test(text);
    if (mathPatterns.test(text) || hasNumbers) {
      return 'mathematical';
    }

    // Code indicators - function, code, program, algorithm, syntax
    const codePatterns = /\b(code|function|program|algorithm|syntax|variable|loop|array|class|method|compile|debug|output|input|return|if\s*\(|for\s*\(|while\s*\(|def\s+|function\s+|const\s+|let\s+|var\s+)\b/i;
    const hasCodeSyntax = /[{}();]|->|=>|::|\.\./.test(text);
    if (codePatterns.test(text) || hasCodeSyntax) {
      return 'code';
    }

    // True/False indicators
    const trueFalsePatterns = /\b(true\s*(?:or|\/)\s*false|true\/false|t\/f|correct\s*(?:or|\/)\s*incorrect)\b/i;
    if (trueFalsePatterns.test(text)) {
      return 'true_false';
    }

    // Multiple choice indicators
    const mcPatterns = /\b(multiple choice|choose|select|pick one|options?:?\s*[A-D]|[A-D]\)|which of the following)\b/i;
    const hasOptions = /^\s*[A-D]\s*[.)\]]/m.test(text);
    if (mcPatterns.test(text) || hasOptions) {
      return 'multiple_choice';
    }

    // Essay by length - if the expected answer is long, treat as essay
    const wordCount = text.split(/\s+/).length;
    if (wordCount > 100) {
      return 'essay';
    }

    // Default to short_answer
    return 'short_answer';
  };

  const parseQuestionBlock = (lines, fallbackNumber) => {
    const question = {
      questionNumber: fallbackNumber.toString(),
      questionType: 'short_answer',
      maxScore: 10,
      rubric: {},
      keywords: [],
      difficulty: 'Medium'
    };

    let questionText = '';
    let inQuestion = false;
    let inAnswer = false;
    let currentAnswer = [];

    lines.forEach(line => {
      // Question identification - more flexible patterns
      const questionMatch = line.match(/^(?:QUESTION|Q)\s*(\d+)(?:\s*\([^)]+\))?\s*[-:]?\s*(.+)/i) ||
                           line.match(/^(\d+)\.\s*(.+)/);
      
      if (questionMatch) {
        // Extract question number and text based on which pattern matched
        question.questionNumber = questionMatch[1] || questionMatch[2] || fallbackNumber.toString();
        questionText = questionMatch[2] || questionMatch[3] || '';
        inQuestion = true;
        
        // Detect question type
        if (line.toLowerCase().includes('multiple choice') || line.toLowerCase().includes('mc')) {
          question.questionType = 'multiple_choice';
        } else if (line.toLowerCase().includes('essay')) {
          question.questionType = 'essay';
        } else if (line.toLowerCase().includes('true/false') || line.toLowerCase().includes('true or false')) {
          question.questionType = 'true_false';
        }
        
        // Extract points from question line
        const pointsMatch = line.match(/(\d+)\s*(?:pts?|points?)/i);
        if (pointsMatch) question.maxScore = parseInt(pointsMatch);
        
        return;
      }

      // Continue building question text
      if (inQuestion && !line.match(/^(?:CORRECT ANSWER|ANSWER|SAMPLE ANSWER|MAX SCORE|RUBRIC|KEYWORDS|DIFFICULTY|PARTIAL CREDIT|EXPLANATION|GRADING):/i)) {
        if (line.match(/^[A-D]\)/)) {
          // Multiple choice options - if we see these, it's definitely MC
          question.questionType = 'multiple_choice';
          questionText += '\n' + line;
        } else if (!line.match(/^\s*$/)) {
          questionText += ' ' + line;
        }
        return;
      }

      // Parse answer
      const answerMatch = line.match(/^(?:CORRECT ANSWER|ANSWER|SAMPLE ANSWER):\s*(.+)/i);
      if (answerMatch) {
        let answerText = answerMatch[1].trim();
        
        // If this is a multiple choice question, normalize the answer to just the letter
        if (question.questionType === 'multiple_choice') {
          const mcLetter = answerText.match(/\b([A-D])\b/i);
          if (mcLetter) {
            answerText = mcLetter[1].toUpperCase();
          }
        }
        
        question.correctAnswer = answerText;
        inAnswer = true;
        return;
      }

      // Continue building answer
      if (inAnswer && !line.match(/^(?:MAX SCORE|RUBRIC|KEYWORDS|DIFFICULTY|PARTIAL CREDIT|EXPLANATION|GRADING):/i)) {
        currentAnswer.push(line);
        return;
      }

      // Parse other attributes
      const maxScoreMatch = line.match(/MAX SCORE:\s*(\d+)/i);
      if (maxScoreMatch) {
        question.maxScore = parseInt(maxScoreMatch);
        inAnswer = false;
        if (currentAnswer.length > 0) {
          question.correctAnswer = (question.correctAnswer || '') + ' ' + currentAnswer.join(' ');
          currentAnswer = [];
        }
        return;
      }

      const rubricMatch = line.match(/RUBRIC:\s*(.+)/i);
      if (rubricMatch) {
        question.rubric = parseRubric(rubricMatch[1]);
        return;
      }

      const keywordsMatch = line.match(/KEYWORDS:\s*(.+)/i);
      if (keywordsMatch) {
        question.keywords = keywordsMatch[1].split(',').map(k => k.trim());
        return;
      }

      const difficultyMatch = line.match(/DIFFICULTY:\s*(.+)/i);
      if (difficultyMatch) {
        question.difficulty = difficultyMatch[1].trim();
        return;
      }

      const partialCreditMatch = line.match(/PARTIAL CREDIT:\s*(.+)/i);
      if (partialCreditMatch) {
        question.partialCredit = partialCreditMatch[1];
        return;
      }

      const explanationMatch = line.match(/EXPLANATION:\s*(.+)/i);
      if (explanationMatch) {
        question.explanation = explanationMatch[1];
        return;
      }

      const gradingMatch = line.match(/GRADING:\s*(.+)/i);
      if (gradingMatch) {
        question.gradingNotes = gradingMatch[1];
        return;
      }
    });

    // Finalize answer if still building
    if (currentAnswer.length > 0) {
      question.correctAnswer = (question.correctAnswer || '') + ' ' + currentAnswer.join(' ');
    }

    question.question = questionText.trim();
    
    return question.question ? question : null;
  };

  const parseRubric = (rubricText) => {
    const rubric = {};
    
    // Handle different rubric formats
    if (rubricText.includes('(') && rubricText.includes('%')) {
      // Format: "Accuracy (40%), Completeness (30%)"
      const parts = rubricText.split(',').map(part => part.trim());
      parts.forEach(part => {
        const match = part.match(/(.+?)\s*\((\d+)%\)/);
        if (match) {
          rubric[match[1].toLowerCase().trim()] = parseInt(match[2]);
        }
      });
    } else if (rubricText.includes('-')) {
      // Format: "- Accuracy (30%)\n- Completeness (25%)"
      const lines = rubricText.split(/[-•]/);
      lines.forEach(line => {
        const match = line.match(/(.+?)\s*\((\d+)%\)/);
        if (match) {
          rubric[match[1].toLowerCase().trim()] = parseInt(match[2]);
        }
      });
    } else {
      // Simple format - assign equal weights
      const criteria = rubricText.split(',').map(c => c.trim());
      const weight = Math.floor(100 / criteria.length);
      criteria.forEach(criterion => {
        rubric[criterion.toLowerCase()] = weight;
      });
    }
    
    return rubric;
  };

  const parseStudentWork = (text) => {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line);
    
    // Extract student metadata
    let studentMetadata = {};
    const studentNameMatch = text.match(/(?:STUDENT|Name):\s*(.+)/i);
    const studentIdMatch = text.match(/STUDENT ID:\s*(.+)/i);
    const classMatch = text.match(/CLASS:\s*(.+)/i);
    const dateMatch = text.match(/(?:SUBMISSION DATE|Date):\s*(.+)/i);
    
    if (studentNameMatch) studentMetadata.name = studentNameMatch[1].trim();
    if (studentIdMatch) studentMetadata.id = studentIdMatch[1].trim();
    if (classMatch) studentMetadata.class = classMatch[1].trim();
    if (dateMatch) studentMetadata.submissionDate = dateMatch[1].trim();
    
    // Parse answers with more flexible patterns
    const answers = [];
    let currentQuestion = null;
    let currentAnswer = [];
    let inAnswer = false;
    
    lines.forEach(line => {
      // Skip metadata lines
      if (line.match(/(?:STUDENT|Name|STUDENT ID|CLASS|SUBMISSION DATE|Date):/i)) {
        return;
      }
      
      // Question answer patterns
      const questionMatch = line.match(/^(?:QUESTION\s*(\d+)\s*ANSWER|(\d+)\.|\s*(\d+)\s*[.:-])\s*(.+)/i);
      
      if (questionMatch) {
        // Save previous answer
        if (currentQuestion && currentAnswer.length > 0) {
          const answerText = currentAnswer.join(' ').trim();
          answers.push({
            questionNumber: currentQuestion,
            answer: normalizeStudentAnswer(answerText)
          });
        }
        
        // Start new answer
        currentQuestion = questionMatch[1] || questionMatch[2] || questionMatch[3];
        const answerStart = questionMatch[4] || '';
        currentAnswer = answerStart ? [answerStart] : [];
        inAnswer = true;
      } else if (inAnswer && line && !line.match(/^(?:QUESTION|Name|STUDENT):/i)) {
        // Continue building current answer
        currentAnswer.push(line);
      } else if (line.match(/^\d+\.\s*[A-D]/i)) {
        // Multiple choice answer - extract just the letter
        const mcMatch = line.match(/^\d+\.\s*([A-D])/i);
        if (mcMatch && currentQuestion) {
          answers.push({
            questionNumber: currentQuestion,
            answer: mcMatch[1].toUpperCase() // Just the letter
          });
          currentQuestion = null;
          currentAnswer = [];
          inAnswer = false;
        }
      }
    });
    
    // Save last answer
    if (currentQuestion && currentAnswer.length > 0) {
      const answerText = currentAnswer.join(' ').trim();
      answers.push({
        questionNumber: currentQuestion,
        answer: normalizeStudentAnswer(answerText)
      });
    }
    
    return {
      studentMetadata,
      answers
    };
  };

  // Normalize student answers - extract just the letter for multiple choice
  const normalizeStudentAnswer = (answer) => {
    if (!answer) return '';
    
    const trimmed = answer.trim();
    
    // Check if this looks like a multiple choice answer
    // Patterns: "B", "B - Chloroplast", "1. B", "Answer: B", etc.
    
    // Pattern 1: "B - Chloroplast" or "B – something" (letter followed by dash)
    const dashPattern = trimmed.match(/^([A-D])\s*[-–—]\s*/i);
    if (dashPattern) {
      return dashPattern[1].toUpperCase();
    }
    
    // Pattern 2: "1. B" or "Q1. B" (numbered with letter)
    const numberedPattern = trimmed.match(/^(?:\d+|Q\d+)\s*\.?\s*([A-D])\s*[-–—]?/i);
    if (numberedPattern) {
      return numberedPattern[1].toUpperCase();
    }
    
    // Pattern 3: Just "B" or "B." (standalone letter)
    const standalonePattern = trimmed.match(/^([A-D])\.?\s*$/i);
    if (standalonePattern) {
      return standalonePattern[1].toUpperCase();
    }
    
    // Pattern 4: "Answer: B" or "Ans: B"
    const answerLabelPattern = trimmed.match(/(?:answer|ans)[:\s]+([A-D])/i);
    if (answerLabelPattern) {
      return answerLabelPattern[1].toUpperCase();
    }
    
    // Pattern 5: Letter at the start followed by text "B Chloroplast" or "B) Chloroplast"
    const startLetterPattern = trimmed.match(/^([A-D])[)\s]\s*/i);
    if (startLetterPattern && trimmed.length < 100) {
      // If the answer is short and starts with a letter, it's likely MC
      return startLetterPattern[1].toUpperCase();
    }
    
    // Pattern 6: Letter anywhere in short answers (for cases like "I chose B")
    if (trimmed.length < 50) {
      const letterMatch = trimmed.match(/\b([A-D])\b/i);
      if (letterMatch && /(?:chose|select|pick|answer|option)\s+[A-D]/i.test(trimmed)) {
        return letterMatch[1].toUpperCase();
      }
    }
    
    // Not a multiple choice answer, return as-is
    return trimmed;
  };

  const loadExample = (format = inputMode) => {
    setAnswerKeyText(examples[format] || examples.structured);
    setStudentWorkText(exampleStudentWork[format] || exampleStudentWork.structured);
    setTimeout(() => parseAndConvert(), 100);
  };

  const detectFormat = (text) => {
    if (!autoDetectFormat) return inputMode;
    
    const hasStructuredKeywords = /(?:QUESTION|CORRECT ANSWER|MAX SCORE|RUBRIC):/i.test(text);
    const hasNumberedQuestions = /^\d+\./m.test(text);
    const hasMixedFormat = /(?:Multiple Choice|Essay|Short Answer)/i.test(text);
    
    if (hasMixedFormat) return 'mixed';
    if (hasStructuredKeywords) return 'structured';
    if (hasNumberedQuestions) return 'freeform';
    return 'structured';
  };

  const parseAndConvert = () => {
    setParsingError(null);
    
    try {
      if (answerKeyText.trim()) {
        const detectedFormat = detectFormat(answerKeyText);
        const answerKeyData = parseNaturalLanguage(answerKeyText, 'answerKey');
        answerKeyData.detectedFormat = detectedFormat;
        onAnswerKeyChange(JSON.stringify(answerKeyData, null, 2));
      }
      
      if (studentWorkText.trim()) {
        const studentWorkData = parseNaturalLanguage(studentWorkText, 'studentWork');
        onStudentWorkChange(JSON.stringify(studentWorkData, null, 2));
      }
    } catch (error) {
      setParsingError(error.message);
    }
  };

  // Auto-parse with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (answerKeyText.trim() || studentWorkText.trim()) {
        parseAndConvert();
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [answerKeyText, studentWorkText]);

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
            Enhanced Natural Language Input
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Supports multiple input formats with automatic detection
          </p>
        </div>
        
        <div className="flex gap-2 items-center">
          <select
            value={inputMode}
            onChange={(e) => setInputMode(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800"
          >
            <option value="structured">Structured</option>
            <option value="freeform">Freeform</option>
            <option value="mixed">Mixed Format</option>
          </select>
          
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={autoDetectFormat}
              onChange={(e) => setAutoDetectFormat(e.target.checked)}
              className="mr-1"
            />
            Auto-detect
          </label>
          
          <button
            onClick={() => loadExample()}
            className="premium-button px-4 py-2 text-sm bg-gradient-to-r from-primary via-purple-600 to-primary bg-[length:200%_auto] hover:bg-[length:100%_auto] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 tracking-wide"
          >
            Load Example
          </button>
        </div>
      </div>

      {parsingError && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <div className="text-red-500 mr-2">⚠️</div>
            <div>
              <div className="font-medium text-red-700 dark:text-red-300">Parsing Error</div>
              <div className="text-sm text-red-600 dark:text-red-400">{parsingError}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Answer Key Input */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Answer Key (Natural Language)
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Format: {autoDetectFormat ? 'Auto-detected' : inputMode}
            </div>
          </div>
          <textarea
            value={answerKeyText}
            onChange={(e) => setAnswerKeyText(e.target.value)}
            placeholder={getPlaceholderText('answerKey')}
            className="w-full h-80 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
            spellCheck="false"
          />
        </div>

        {/* Student Work Input */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Student Work (Natural Language)
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Auto-converts to JSON
            </div>
          </div>
          <textarea
            value={studentWorkText}
            onChange={(e) => setStudentWorkText(e.target.value)}
            placeholder={getPlaceholderText('studentWork')}
            className="w-full h-80 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
            spellCheck="false"
          />
        </div>
      </div>

      {/* Enhanced Tips Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-start">
            <div className="text-blue-500 mr-2 mt-0.5">📋</div>
            <div>
              <div className="font-medium text-blue-700 dark:text-blue-300">Structured Format</div>
              <ul className="text-sm text-blue-600 dark:text-blue-400 mt-1 space-y-1">
                <li>• QUESTION 1: [question]</li>
                <li>• CORRECT ANSWER: [answer]</li>
                <li>• MAX SCORE: [points]</li>
                <li>• RUBRIC: [criteria]</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="flex items-start">
            <div className="text-green-500 mr-2 mt-0.5">✏️</div>
            <div>
              <div className="font-medium text-green-700 dark:text-green-300">Freeform Format</div>
              <ul className="text-sm text-green-600 dark:text-green-400 mt-1 space-y-1">
                <li>• 1. [question text]</li>
                <li>• Natural descriptions</li>
                <li>• Flexible point values</li>
                <li>• Conversational style</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <div className="flex items-start">
            <div className="text-purple-500 mr-2 mt-0.5">🎯</div>
            <div>
              <div className="font-medium text-purple-700 dark:text-purple-300">Mixed Format</div>
              <ul className="text-sm text-purple-600 dark:text-purple-400 mt-1 space-y-1">
                <li>• Multiple choice</li>
                <li>• Short answer</li>
                <li>• Essay questions</li>
                <li>• Combined formats</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  function getPlaceholderText(type) {
    if (type === 'answerKey') {
      switch (inputMode) {
        case 'freeform':
          return `Biology Quiz

1. Photosynthesis question (10 pts):
Students should explain how plants make food...

2. What are the main stages? (8 points)
Answer should include glycolysis, Krebs cycle...`;
        case 'mixed':
          return `EXAM: Biology Test
Q1 (Multiple Choice - 5 pts): Which organelle...?
A) Option A  B) Option B
ANSWER: B

Q2 (Essay - 25 pts): Compare photosynthesis...
RUBRIC: Content (40%), Analysis (35%), Writing (25%)`;
        default:
          return `QUESTION 1: [Your question]
CORRECT ANSWER: [Correct answer]
MAX SCORE: [Number]
RUBRIC: [Criteria (weight%)]
KEYWORDS: [key, terms]
DIFFICULTY: [Easy/Medium/Hard]

QUESTION 2: ...`;
      }
    } else {
      return `STUDENT: [Name]
STUDENT ID: [ID]

QUESTION 1 ANSWER: [Student's response]

QUESTION 2 ANSWER: [Student's response]`;
    }
  }
};

export default NaturalLanguageInput;